import { useRef, useEffect } from "react";

const panel = {
  title: "Through the Universe of Intelligence.",
  sub: "Every layer — designed, engineered, alive.",
  accent: "#00E5FF",
};

export default function ImmersiveShowcase() {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);

  // Video-first reveal: when section enters viewport, wait a bit so video establishes,
  // then fade the text in softly. No scroll binding, no sticky — pure passive in-view trigger.
  useEffect(() => {
    if (!sectionRef.current || !contentRef.current) return;
    let revealTimer = null;

    // Pre-hide the text until the reveal fires
    contentRef.current.style.opacity = "0";
    contentRef.current.style.transform = "translateY(30px) scale(0.96)";
    contentRef.current.style.filter = "blur(6px)";

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          revealTimer = setTimeout(() => {
            if (!contentRef.current) return;
            contentRef.current.style.transition =
              "opacity 1.2s cubic-bezier(0.22,1,0.36,1), transform 1.2s cubic-bezier(0.22,1,0.36,1), filter 1.2s cubic-bezier(0.22,1,0.36,1)";
            contentRef.current.style.opacity = "1";
            contentRef.current.style.transform = "translateY(0px) scale(1)";
            contentRef.current.style.filter = "blur(0px)";
          }, 300);
          observer.disconnect();
        }
      },
      { rootMargin: "-15% 0px -15% 0px" }
    );

    observer.observe(sectionRef.current);
    return () => {
      observer.disconnect();
      if (revealTimer) clearTimeout(revealTimer);
    };
  }, []);

  return (
    <section
      id="showcase"
      ref={sectionRef}
      data-testid="immersive-showcase-section"
      className="relative h-screen w-full overflow-hidden"
    >
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ opacity: 0.2, mixBlendMode: "lighten" }}
      >
        <source src="https://customer-assets.emergentagent.com/job_deva-command-nexus/artifacts/tvanmclv_VID-20260418-WA00022.mp4" type="video/mp4" />
      </video>

      {/* Ambient Glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] md:w-[600px] md:h-[600px] rounded-full blur-[150px]"
          style={{ backgroundColor: `${panel.accent}0D` }}
        />
      </div>

      {/* Panel Content — centered, delayed fade-in after video establishes */}
      <div
        ref={contentRef}
        className="absolute inset-0 flex flex-col items-center justify-center text-center px-8"
        style={{ willChange: "opacity, transform, filter" }}
      >
        <h2 className="showcase-heading pop-click font-heading text-4xl sm:text-5xl lg:text-6xl font-extralight tracking-tighter text-white mb-5">
          Through the Universe of{" "}
          <span
            className="showcase-accent bg-gradient-to-r from-[#00E5FF] via-[#7A5CFF] to-[#FF2D9A] bg-clip-text text-transparent inline-block"
          >
            Intelligence.
          </span>
        </h2>
        <p className="showcase-sub pop-click font-body text-base md:text-lg text-zinc-400 font-light max-w-md">
          Every layer — designed, engineered, alive.
        </p>
        <div
          className="mt-10 w-12 h-px"
          style={{ backgroundColor: `${panel.accent}50` }}
        />
      </div>

      {/* System Label */}
      <div className="absolute top-28 left-0 right-0 md:left-12 md:right-auto z-20 pointer-events-none">
        <p className="sys-label text-center md:text-left">SYS.SEQUENCE // ACTIVE</p>
      </div>
    </section>
  );
}
